import { Library, Loader2, Send, Target, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCurrentPurpose } from '@/store/useBookAiPurposeStore'
import { useBookAiStore } from '@/store/useBookAiStore'
import { useLibraryChatStore } from '@/store/useLibraryChatStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { AssistantBubble, SuggestionChips, ThinkingIndicator, UserBubble } from './chat-parts'
import { CitationChip } from './citation-chip'
import { LibraryChat } from './library-chat'
import type { BookAiReaderState } from './useBookAi'

// 入力欄の文字数の上限（引用は useBookAiStore で 300 字に切り詰める。質問全体はサーバー側で 1000 字まで）
const MAX_TYPED_CHARS = 600
const QUOTE_DEFAULT_QUESTION = 'この箇所を分かりやすく説明して'

const SUGGESTIONS = [
  'ここまでの要点を3つにまとめて',
  'この章で一番大事なことは？',
  'この本の主張をひとことで言うと？',
  'ここまでの内容を具体例で説明して',
]

/**
 * 質問を送る（ネタバレ防止が有効なら、今読んでいる位置までの本文だけで答えさせる）
 * 本棚全体への質問にしているときは、AI 解析済みのすべての本から答えさせる
 */
const useAskBook = (bookAi: BookAiReaderState) => {
  const sendQuestion = useBookAiStore((state) => state.sendQuestion)
  const sendLibrary = useLibraryChatStore((state) => state.send)
  const libraryMode = useUISettingsStore((state) => state.bookChatLibraryMode)
  const spoilerGuard = useUISettingsStore((state) => state.bookChatSpoilerGuard)
  return useCallback(
    async (question: string) => {
      if (libraryMode) return sendLibrary(question)
      // 位置が分からないまま送ると本全体から答えてしまうので、ネタバレ防止中は送らない
      if (spoilerGuard && bookAi.currentKey == null) {
        toast.info('読んでいる位置がまだ分からないため、少し読み進めてから質問してください')
        return false
      }
      return sendQuestion(question, spoilerGuard ? bookAi.currentKey : null)
    },
    [sendQuestion, sendLibrary, libraryMode, spoilerGuard, bookAi.currentKey],
  )
}

type ChatTabProps = {
  bookAi: BookAiReaderState
  onNavigate: (key: number) => void
}

/** 本と対話するチャット（履歴と根拠の表示）。入力欄はシート下端の ChatInput */
export const ChatTab = ({ bookAi, onNavigate }: ChatTabProps) => {
  const messages = useBookAiStore((state) => state.messages)
  const isSending = useBookAiStore((state) => state.isSending)
  const loadChat = useBookAiStore((state) => state.loadChat)
  const clearChat = useBookAiStore((state) => state.clearChat)
  const spoilerGuard = useUISettingsStore((state) => state.bookChatSpoilerGuard)
  const setSpoilerGuard = useUISettingsStore((state) => state.setBookChatSpoilerGuard)
  const libraryMode = useUISettingsStore((state) => state.bookChatLibraryMode)
  const setLibraryMode = useUISettingsStore((state) => state.setBookChatLibraryMode)
  const libraryMessages = useLibraryChatStore((state) => state.messages)
  const clearLibrary = useLibraryChatStore((state) => state.clear)
  const purpose = useCurrentPurpose(bookAi.episodeId)?.purpose
  const ask = useAskBook(bookAi)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadChat()
  }, [loadChat])

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, isSending])

  const libraryToggle = (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <Switch checked={libraryMode} onCheckedChange={setLibraryMode} />
      <Library className="size-3.5" />
      本棚全体に質問する
    </label>
  )

  if (libraryMode) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          {libraryToggle}
          {libraryMessages.length > 0 && (
            <Button variant="ghost" size="sm" className="px-2!" onClick={clearLibrary}>
              <Trash2 className="size-4" />
              消去
            </Button>
          )}
        </div>
        <LibraryChat episodeId={bookAi.episodeId} onNavigate={onNavigate} />
      </div>
    )
  }

  if (!bookAi.status?.chat_ready) {
    return (
      <div className="space-y-3">
        {libraryToggle}
        <p className="py-8 text-center text-sm text-muted-foreground">AI 解析が終わると、この本について質問できます</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch checked={spoilerGuard} onCheckedChange={setSpoilerGuard} />
            読んだ所までで答える（ネタバレ防止）
          </label>
          {libraryToggle}
        </div>
        {messages.length > 0 && (
          <Button variant="ghost" size="sm" className="px-2!" onClick={clearChat}>
            <Trash2 className="size-4" />
            消去
          </Button>
        )}
      </div>

      {purpose && (
        <p className="flex items-start gap-1.5 rounded-lg bg-purple-500/5 px-2.5 py-1.5 text-xs text-muted-foreground">
          <Target className="mt-0.5 size-3.5 shrink-0 text-purple-500" />
          <span className="wrap-break-word">目的「{purpose}」に関係する質問は、目的に役立つ観点で答えます</span>
        </p>
      )}

      {messages.length === 0 && !isSending && (
        <SuggestionChips intro="この本の内容をもとに答えます。たとえば:" suggestions={SUGGESTIONS} onPick={ask} />
      )}

      <div className="space-y-3">
        {messages.map((message) =>
          message.role === 'user' ? (
            <UserBubble key={message.id} content={message.content} />
          ) : (
            <AssistantBubble
              key={message.id}
              content={message.content}
              citations={
                message.citations.length > 0 &&
                message.citations.map((citation) => (
                  <CitationChip key={citation.chunk_id} citation={citation} onClick={() => onNavigate(citation.key)} />
                ))
              }
            />
          ),
        )}
        {isSending && <ThinkingIndicator label="本文を探して考えています…" />}
        <div ref={endRef} />
      </div>
    </div>
  )
}

/** シート下端に固定するチャットの入力欄 */
export const ChatInput = ({ bookAi }: { bookAi: BookAiReaderState }) => {
  const libraryMode = useUISettingsStore((state) => state.bookChatLibraryMode)
  const isBookSending = useBookAiStore((state) => state.isSending)
  const isLibrarySending = useLibraryChatStore((state) => state.isSending)
  const isSending = libraryMode ? isLibrarySending : isBookSending
  const chatQuote = useBookAiStore((state) => state.chatQuote)
  const setChatQuote = useBookAiStore((state) => state.setChatQuote)
  const ask = useAskBook(bookAi)
  const [draft, setDraft] = useState('')
  const canSend = (!!draft.trim() || !!chatQuote) && !isSending

  const submit = async () => {
    if (!canSend) return
    const typed = draft.trim()
    const quote = chatQuote
    // 本文で選んだ箇所があれば引用して聞く（質問が空なら説明を求める）
    const question = quote ? `「${quote}」\n${typed || QUOTE_DEFAULT_QUESTION}` : typed
    setDraft('')
    setChatQuote(null)
    const sent = await ask(question)
    // 送れなかったときは書いた内容と引用を戻す
    if (!sent) {
      setDraft(typed)
      setChatQuote(quote)
    }
  }

  return (
    <div className="px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      {chatQuote && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border-l-4 border-primary bg-muted/60 px-2 py-1.5 text-xs">
          <span className="line-clamp-2 flex-1 wrap-break-word">{chatQuote}</span>
          <button
            type="button"
            aria-label="引用を外す"
            className="shrink-0 text-muted-foreground"
            onClick={() => setChatQuote(null)}
          >
            <X className="size-4" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              submit()
            }
          }}
          placeholder={
            chatQuote
              ? 'この箇所について質問（空のまま送ると説明します）'
              : libraryMode
                ? '本棚のすべての本について質問する'
                : 'この本について質問する'
          }
          rows={1}
          maxLength={MAX_TYPED_CHARS}
          className="max-h-32 min-h-10 flex-1 resize-none"
        />
        <Button
          size="icon"
          aria-label="送信"
          disabled={!canSend}
          onClick={submit}
          className={cn('shrink-0', isSending && 'opacity-60')}
        >
          {isSending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </Button>
      </div>
    </div>
  )
}
